import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Crown, User as UserIcon, UserPlus } from "lucide-react";
import Board from "@/components/game/Board";
import HUD from "@/components/game/HUD";
import CluePanel from "@/components/game/CluePanel";
import EndModal from "@/components/game/EndModal";
import { endTurn, resetGame, revealCard, sendClue, setRole } from "@/lib/api";
import { useNavigate } from "react-router";

export default function Game({ room, self, refresh }) {
  const [busy, setBusy] = useState(false);
  const [lastRevealedIdx, setLastRevealedIdx] = useState(null);
  const nav = useNavigate();
  const board = room.board;
  const isShared = room.mode === "shared";
  const isSpectator = !self.role;
  const isSpymaster = self.role === "spymaster";
  const isAgent = self.role === "agent";

  // In shared mode, the device's effective team is whoever has the turn.
  const myTurn = isShared
    ? (isSpymaster && board.phase === "clue") || (isAgent && board.phase === "guess")
    : board.turn === self.team;

  const canClue = isSpymaster && board.phase === "clue" && (isShared || board.turn === self.team);
  const canReveal = isAgent && board.phase === "guess" && (isShared || board.turn === self.team);

  // Track newly revealed for flip animation
  useEffect(() => {
    if (!board) return;
    const revealed = board.cards.filter((c) => c.revealed);
    if (revealed.length > 0) {
      const latest = revealed[revealed.length - 1];
      setLastRevealedIdx(latest.index);
      const t = setTimeout(() => setLastRevealedIdx(null), 500);
      return () => clearTimeout(t);
    }
  }, [board?.cards.map((c) => c.revealed).join(",")]); // eslint-disable-line

  const onCard = async (idx) => {
    if (!canReveal) {
      if (isSpectator) return toast.info("Estás en modo espectador");
      if (!isAgent) return toast.error("Solo los agentes pueden tocar cartas");
      if (board.phase !== "guess") return toast.error("Esperando la pista del Jefe");
      if (!isShared && board.turn !== self.team) return toast.error("No es el turno de tu equipo");
      return;
    }
    setBusy(true);
    try {
      await revealCard(room.code, { player_id: self.id, index: idx });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo revelar");
    } finally { setBusy(false); }
  };

  const onClue = async ({ word, number, unlimited }) => {
    setBusy(true);
    try {
      await sendClue(room.code, { player_id: self.id, word, number, unlimited });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Pista inválida");
    } finally { setBusy(false); }
  };

  const onPass = async () => {
    setBusy(true);
    try {
      await endTurn(room.code, self.id);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo pasar");
    } finally { setBusy(false); }
  };

  const onReset = async () => {
    setBusy(true);
    try {
      await resetGame(room.code, self.id);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo reiniciar");
    } finally { setBusy(false); }
  };

  const joinAsAgent = async (team) => {
    setBusy(true);
    try {
      await setRole(room.code, { player_id: self.id, team, role: "agent" });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo unir");
    } finally { setBusy(false); }
  };

  const joinSharedDevice = async (role) => {
    setBusy(true);
    try {
      await setRole(room.code, { player_id: self.id, team: null, role });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo unir");
    } finally { setBusy(false); }
  };

  const lastLog = useMemo(() => (board.log || []), [board.log]);

  // Active team banner (very prominent in shared mode)
  const turnColor = board.turn === "red" ? "var(--red)" : "var(--blue)";
  const turnLabel = board.turn === "red" ? "ROJO" : "AZUL";
  const smRedName = room.options?.spymaster_red_name;
  const smBlueName = room.options?.spymaster_blue_name;
  const activeSpymasterName = board.turn === "red" ? smRedName : smBlueName;

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4 pb-40">
      {/* Big shared-mode banner */}
      {isShared && board.phase !== "ended" && (
        <div
          data-testid="active-team-banner"
          className="mb-3 border-2 px-4 py-3 flex items-center justify-between gap-3"
          style={{
            borderColor: turnColor,
            background: `linear-gradient(90deg, ${turnColor}33, transparent)`,
          }}
        >
          <div className="min-w-0">
            <div className="label-classified" style={{ color: turnColor }}>
              {board.phase === "clue" ? "Pasa el móvil al Jefe" : "Pasa el móvil al equipo"}
            </div>
            <div className="font-heading text-2xl sm:text-3xl uppercase truncate" style={{ color: turnColor }}>
              {board.phase === "clue" && activeSpymasterName
                ? <span data-testid="active-spymaster-name">{activeSpymasterName}</span>
                : <>EQUIPO {turnLabel}</>}
            </div>
            {board.phase === "clue" && activeSpymasterName && (
              <div className="label-classified mt-0.5" style={{ color: turnColor }}>(JEFE {turnLabel})</div>
            )}
          </div>
          <div className="text-right shrink-0">
            <div className="label-classified">{board.phase === "clue" ? "Da la pista" : "Adivinen"}</div>
            <div className="font-heading text-lg sm:text-xl uppercase" style={{ color: turnColor }}>
              {isSpymaster ? <span className="inline-flex items-center gap-1"><Crown size={16} /> Jefe</span> : isAgent ? <span className="inline-flex items-center gap-1"><UserIcon size={16} /> Agente</span> : "Espectador"}
            </div>
          </div>
        </div>
      )}

      <HUD room={room} self={self} />
      <Board
        cards={board.cards}
        onCard={onCard}
        isSpymaster={isSpymaster}
        disabled={busy || board.phase === "ended" || isSpectator}
        lastRevealedIdx={lastRevealedIdx}
        myTurn={myTurn}
        turn={board.turn}
        phase={board.phase}
      />

      {/* Spectator banner & quick join */}
      {isSpectator && board.phase !== "ended" && (
        <div data-testid="spectator-banner" className="mt-3 border border-[color:var(--amber)] bg-[color:var(--surface)] p-3">
          <div className="label-classified inline-flex items-center gap-2" style={{ color: "var(--amber)" }}>
            <UserPlus size={14} /> Modo espectador
          </div>
          <div className="text-sm text-[color:var(--text-dim)] mt-1 mb-2">
            {isShared
              ? "La partida ya empezó. Si un móvil está libre, puedes ocuparlo."
              : "La partida ya empezó. Únete como agente al equipo que prefieras."}
          </div>
          {isShared ? (
            <div className="flex gap-2 flex-wrap">
              {!room.players.some((p) => p.role === "spymaster") && (
                <button data-testid="btn-spectator-join-spymaster" className="btn-ghost text-xs" onClick={() => joinSharedDevice("spymaster")} disabled={busy}>
                  Soy el móvil de los Jefes
                </button>
              )}
              {!room.players.some((p) => p.role === "agent") && (
                <button data-testid="btn-spectator-join-agent" className="btn-ghost text-xs" onClick={() => joinSharedDevice("agent")} disabled={busy}>
                  Soy el móvil de los Agentes
                </button>
              )}
              {room.players.filter((p) => p.role).length >= 2 && (
                <span className="text-xs text-[color:var(--text-dim)]">Ambos móviles ocupados.</span>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              <button data-testid="btn-join-red" className="btn-red text-xs" onClick={() => joinAsAgent("red")} disabled={busy}>Unirme al Rojo</button>
              <button data-testid="btn-join-blue" className="btn-blue text-xs" onClick={() => joinAsAgent("blue")} disabled={busy}>Unirme al Azul</button>
            </div>
          )}
        </div>
      )}

      {/* Log */}
      <div className="mt-4 border border-[color:var(--border)] bg-[color:var(--surface)] p-3">
        <div className="label-classified mb-1">Registro</div>
        {lastLog.length === 0 ? (
          <div className="text-xs text-[color:var(--text-dim)]">— sin eventos —</div>
        ) : (
          <ul className="text-xs font-mono space-y-1">
            {lastLog.map((l, i) => (
              <li key={i} className={l.team === "red" ? "text-red-400" : l.team === "blue" ? "text-blue-400" : "text-[color:var(--text-dim)]"}>
                <span className="text-[color:var(--text-dim)] mr-1">[{l.ts ? new Date(l.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}]</span>
                <span className={l.team === "red" ? "text-red-400" : l.team === "blue" ? "text-blue-400" : ""}>› {l.text}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <CluePanel
        room={room}
        self={self}
        canClue={canClue}
        canReveal={canReveal}
        isSpectator={isSpectator}
        onClue={onClue}
        onPass={onPass}
        busy={busy}
      />

      {board.phase === "ended" && (
        <EndModal
          board={board}
          self={self}
          onReset={self.is_host ? onReset : null}
          onLeave={() => nav("/")}
        />
      )}
    </div>
  );
}
